import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, 
  Send, 
  Eye, 
  MousePointer,
  Users,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader2,
  Cloud,
  CloudOff,
  Mail,
  Trophy,
  Target,
  Percent,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { mauticApi } from '../lib/api';

export const Statistics = () => {
  const [emails, setEmails] = useState([]);
  const [contacts, setContacts] = useState({ total: 0 });
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isMauticConnected, setIsMauticConnected] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [emailsRes, contactsRes, segmentsRes] = await Promise.all([
        mauticApi.getEmails({ limit: 100 }),
        mauticApi.getContacts({ limit: 1 }),
        mauticApi.getSegments()
      ]);
      
      setIsMauticConnected(!emailsRes.data.simulated);
      setEmails(emailsRes.data.emails || []);
      setContacts({ total: parseInt(contactsRes.data.total) || 0 });
      setSegments(segmentsRes.data.segments || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Calculate statistics
  const totalEmails = emails.length;
  const totalSent = emails.reduce((sum, e) => sum + (e.sentCount || 0), 0);
  const totalRead = emails.reduce((sum, e) => sum + (e.readCount || 0), 0);
  const avgOpenRate = totalSent > 0 ? ((totalRead / totalSent) * 100) : 0;
  
  // Top performing emails (by open rate, min 1 send)
  const topEmails = [...emails]
    .filter(e => e.sentCount > 0)
    .map(e => ({
      ...e,
      openRate: e.sentCount > 0 ? (e.readCount / e.sentCount) * 100 : 0
    }))
    .sort((a, b) => b.openRate - a.openRate)
    .slice(0, 5);

  // Most sent emails
  const mostSentEmails = [...emails]
    .filter(e => e.sentCount > 0)
    .sort((a, b) => b.sentCount - a.sentCount)
    .slice(0, 5);

  // Emails with best absolute opens
  const mostOpenedEmails = [...emails]
    .filter(e => e.readCount > 0)
    .sort((a, b) => b.readCount - a.readCount)
    .slice(0, 5);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-slate-500">Cargando estadísticas de Mautic...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in" data-testid="statistics-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold font-[Manrope] text-slate-900">Estadísticas</h1>
            {isMauticConnected ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <Cloud className="w-3 h-3 mr-1" />
                Mautic conectado
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-slate-50 text-slate-500">
                <CloudOff className="w-3 h-3 mr-1" />
                Datos simulados
              </Badge>
            )}
          </div>
          <p className="text-slate-500 mt-1">
            Métricas y rendimiento de tus campañas de email marketing
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-stats-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
                Contactos
              </Badge>
            </div>
            <p className="text-3xl font-bold text-slate-900">{contacts.total}</p>
            <p className="text-sm text-slate-500 mt-1">Total en Mautic</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Send className="w-6 h-6 text-emerald-600" />
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                Enviados
              </Badge>
            </div>
            <p className="text-3xl font-bold text-slate-900">{totalSent}</p>
            <p className="text-sm text-slate-500 mt-1">Emails enviados</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                Abiertos
              </Badge>
            </div>
            <p className="text-3xl font-bold text-slate-900">{totalRead}</p>
            <p className="text-sm text-slate-500 mt-1">Total de aperturas</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Percent className="w-6 h-6 text-amber-600" />
              </div>
              <Badge variant="outline" className="bg-amber-50 text-amber-700">
                Tasa
              </Badge>
            </div>
            <p className="text-3xl font-bold text-slate-900">{avgOpenRate.toFixed(1)}%</p>
            <p className="text-sm text-slate-500 mt-1">Tasa de apertura promedio</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">Total Campañas</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{totalEmails}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Target className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">Segmentos</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{segments.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">Promedio envíos/email</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {totalEmails > 0 ? (totalSent / totalEmails).toFixed(1) : 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Best Open Rate */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              <CardTitle className="text-lg">Top Tasa de Apertura</CardTitle>
            </div>
            <CardDescription>Emails con mejor rendimiento</CardDescription>
          </CardHeader>
          <CardContent>
            {topEmails.length > 0 ? (
              <div className="space-y-4">
                {topEmails.map((email, index) => (
                  <div key={email.id} className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${index === 0 ? 'bg-amber-100 text-amber-700' : 
                        index === 1 ? 'bg-slate-200 text-slate-700' :
                        index === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}
                    >
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate" title={email.name}>
                        {email.name || email.subject}
                      </p>
                      <p className="text-xs text-slate-500">
                        {email.sentCount} enviados · {email.readCount} abiertos
                      </p>
                    </div>
                    <Badge 
                      className={
                        email.openRate >= 50 ? 'bg-green-100 text-green-700' :
                        email.openRate >= 30 ? 'bg-emerald-100 text-emerald-700' :
                        email.openRate >= 20 ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }
                    >
                      {email.openRate.toFixed(1)}%
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No hay datos suficientes</p>
            )}
          </CardContent>
        </Card>

        {/* Most Sent */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-500" />
              <CardTitle className="text-lg">Más Enviados</CardTitle>
            </div>
            <CardDescription>Emails con más envíos</CardDescription>
          </CardHeader>
          <CardContent>
            {mostSentEmails.length > 0 ? (
              <div className="space-y-4">
                {mostSentEmails.map((email, index) => (
                  <div key={email.id} className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${index === 0 ? 'bg-emerald-100 text-emerald-700' : 
                        index === 1 ? 'bg-slate-200 text-slate-700' :
                        index === 2 ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}
                    >
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate" title={email.name}>
                        {email.name || email.subject}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={(email.readCount / email.sentCount) * 100} className="h-1.5 flex-1" />
                        <span className="text-xs text-slate-500">
                          {((email.readCount / email.sentCount) * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">{email.sentCount}</p>
                      <p className="text-xs text-slate-500">envíos</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No hay datos suficientes</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Most Opened */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-500" />
            <CardTitle className="text-lg">Más Abiertos</CardTitle>
          </div>
          <CardDescription>Emails con más aperturas totales</CardDescription>
        </CardHeader>
        <CardContent>
          {mostOpenedEmails.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {mostOpenedEmails.map((email, index) => (
                <div key={email.id} className="p-4 rounded-lg bg-slate-50 border">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                      ${index === 0 ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600'}`}
                    >
                      {index + 1}
                    </div>
                    <Eye className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="font-medium text-sm truncate mb-1" title={email.name}>
                    {email.name || email.subject}
                  </p>
                  <p className="text-2xl font-bold text-blue-600">{email.readCount}</p>
                  <p className="text-xs text-slate-500">aperturas</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No hay datos suficientes</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Statistics;
